/* eslint-disable no-unused-vars */
/* eslint-disable no-unsafe-optional-chaining */
import { yupResolver } from "@hookform/resolvers/yup";
import Loader from "common/Loader";
import { setToaster } from "common/toastStore";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import AdminServices from "services/admin-services";
import TokenService from "services/TokenService";
import { AddJobModel } from "types/customers.type";
import * as Yup from "yup";
import { FileUpload } from "primereact/fileupload";
import { handlingUrlParams } from "Utils/AppUtils";

interface AddJobProps {
  onClose: () => void;
  customerId: number;
  onSubmitSuccess: () => void;
}

const AddJob: FunctionComponent<AddJobProps> = ({
  onClose,
  customerId,
  onSubmitSuccess,
}) => {
  const [mergeData, setMergeData] = useState([] as any);
  const [loading, setLoading] = useState<boolean>(false);
  const [showMergeTypes, setShowMergeTypes] = useState<boolean>(false);
  const [selectedMergeOption, setSelectedMergeOption] = useState<any>(null);
  const [packageFieldToken, setPackageFieldToken] = useState(
    "{{Loan.LoanNumber}}_{{Fields.4002}}"
  );
  const [customerData, setCustomerData] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const getCusomerObj = TokenService.getCustomerData();
  const [inputValue, setInputValue] = useState<AddJobModel>({
    customerId: 0,
    name: "",
    description: "",
    mergeOptionId: 0,
    startDate: "",
    endDate: "",
    statusId: 0,
    packageName: "",
    documentTypes: null,
    loanNumbers: null,
  });

  // ---------------- File States ----------------
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [loanFile, setLoanFile] = useState<File | null>(null);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({
      ...inputValue,
      [name]: value,
    });
  };

  // ---------------- FIXED VALIDATION SCHEMA ----------------
  const validationSchema = Yup.object().shape({
    customerId: Yup.number()
      .typeError("Please select a Customer")
      .required("Customer ID is required"),

    name: Yup.string().required("Name is required"),

    description: Yup.string().required("Description is required"),

    mergeOptionId: Yup.number()
      .typeError("Please select a Merge Option")
      .required("Merge Option is required"),

    // packageName: Yup.string().required("Package Name is required"),

    documentTypes: Yup.mixed<File | null>()
      .nullable()
      .test(
        "required",
        "Document file is required",
        (value) => value instanceof File
      )
      .test("fileSize", "File too large (max 1MB)", (value) =>
        value instanceof File ? value.size <= 1000000 : true
      )
      .test("fileType", "Unsupported file type (CSV, Excel, images)", (value) =>
        value instanceof File
          ? [
              "text/csv",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "image/jpeg",
              "image/png",
              "image/jpg",
            ].includes(value.type)
          : true
      ),

    loanNumbers: Yup.mixed<File | null>()
      .nullable()
      .test(
        "required",
        "Loan file is required",
        (value) => value instanceof File
      )
      .test("fileSize", "File too large (max 1MB)", (value) =>
        value instanceof File ? value.size <= 1000000 : true
      )
      .test("fileType", "Unsupported file type (CSV, Excel, images)", (value) =>
        value instanceof File
          ? [
              "text/csv",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "image/jpeg",
              "image/png",
              "image/jpg",
            ].includes(value.type)
          : true
      ),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "all",
  };
  const { register, handleSubmit, formState, setValue, trigger, clearErrors } =
    useForm<AddJobModel>(formOptions as object);
  const { errors } = formState;

  const onSubmit = (data: any) => {
    setLoading(true);
    console.log(data);
    const queryPayload = {
      customerId: data.customerId,
      name: data.name,
      description: data.description,
      mergeOptionId: data.mergeOptionId,
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      statusId: 1,
      packageName:
        selectedMergeOption?.label === "Merge By Place Holder"
          ? `{{Loan.LoanNumber}}/{{placeholder}}${
              data.packageName ? `_${data.packageName}` : ""
            }.pdf`
          : `${packageFieldToken}${
              data.packageName ? `_${data.packageName}` : ""
            }.pdf`,
    };

    const params = handlingUrlParams(queryPayload);

    const formData = new FormData();

    if (documentFile) formData.append("JobDocumentFile", documentFile);
    if (loanFile) formData.append("JobLoanFile", loanFile);

    AdminServices.addJobs(params, formData)
      .then((_response: any) => {
        setLoading(false);
        setToaster({
          severity: "success",
          summary: "Success",
          detail: `Package added successfully!`,
        });
        onClose();
        onSubmitSuccess();
      })
      .catch((e: any) => {
        setLoading(false);
        setToaster({
          severity: "error",
          summary: "Error Message",
          detail: `${e?.response?.data || "Something went wrong"}`,
        });
      });
  };

  useEffect(() => {
    getCusomerData();
    getPackageMergeTypes();
  }, []);

  const getCusomerData = () => {
    let customerDataArray: { label: string; value: number }[] = [];
    getCusomerObj?.forEach((element: { name: string; id: number }) => {
      customerDataArray.push({
        label: element.name,
        value: element.id,
      });
    });
    setCustomerData(customerDataArray);

    if (customerDataArray.length === 1) {
      const singleCustomer = customerDataArray[0];
      setSelectedCustomer(singleCustomer);
      setValue("customerId", singleCustomer.value, { shouldValidate: true });
    }
  };

  const getPackageMergeTypes = () => {
    setLoading(true);
    let MergerData: { label: string; value: number }[] = [];
    AdminServices.getPackageMergeTypes()
      .then((response: any) => {
        const valueData = response?.data || [];
        valueData
          .filter((element: { name: string }) => {
            return !(
              element?.name &&
              element.name.toString().trim().toLowerCase() === "no merge"
            );
          })
          .forEach((element: { name: string; id: number }) => {
            MergerData.push({
              label: element.name,
              value: element.id,
            });
          });
        setMergeData(MergerData);
        setLoading(false);
        setShowMergeTypes(true);
      })
      .catch((e: Error) => {
        console.log(e);
      });
  };

  const handleMergeChange = (selected: any) => {
    setSelectedMergeOption(selected);
    setValue("mergeOptionId", selected?.value ?? 0, { shouldValidate: true });
    console.log("Selected Merge Option:", selected);
  };

  const handleCustomerChange = (selected: any) => {
    setSelectedCustomer(selected);
    setValue("customerId", selected?.value ?? 0, { shouldValidate: true });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="py-5">
        <div className="grid grid-cols-1 gap-8">
          <div className="select-wrapper">
            <div
              className={`select-float-label ${
                selectedCustomer ? "filled" : ""
              }`}
            >
              <Select
                options={customerData}
                placeholder=""
                value={selectedCustomer}
                onChange={handleCustomerChange}
                name="customerId"
                classNamePrefix="react-select"
              />
              <label htmlFor="customerId">Select Customer ID</label>
            </div>
            {errors.customerId && (
              <div className="text-xs text-red-900 mt-1">
                {errors.customerId.message}
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <div className="p-float-label">
              <InputText
                id="name"
                {...register("name")}
                name="name"
                onChange={handleChange}
                className="w-full text-sm"
              />
              <label htmlFor="name">Enter Name</label>
            </div>
            <div className="text-xs text-red-900">{errors?.name?.message}</div>
          </div>

          {/* Description */}
          <div>
            <div className="p-float-label">
              <InputText
                id="description"
                {...register("description")}
                name="description"
                onChange={handleChange}
                className="w-full text-sm"
              />
              <label htmlFor="description">Enter Description</label>
            </div>
            <div className="text-xs text-red-900">
              {errors?.description?.message}
            </div>
          </div>

          {/* Merge Option */}
          {showMergeTypes && (
            <>
              <div>
                <div className="select-wrapper">
                  <div
                    className={`select-float-label ${
                      selectedMergeOption ? "filled" : ""
                    }`}
                  >
                    <Select
                      options={mergeData}
                      placeholder=""
                      value={selectedMergeOption}
                      onChange={handleMergeChange}
                      name="mergeOptionId"
                      classNamePrefix="react-select"
                    />
                    <label htmlFor="mergeOptionId">Select Merge Option</label>
                  </div>
                </div>
                {errors.mergeOptionId && (
                  <div className="text-xs text-red-900 mt-1">
                    {errors.mergeOptionId.message}
                  </div>
                )}
              </div>
            </>
          )}
          {selectedMergeOption && selectedMergeOption?.value !== 4 && (
            <div>
              <label
                htmlFor="packageName"
                className="text-sm py-1 block font-normal"
              >
                Optionally Append to Package Name
              </label>
              <div className="flex flex-row items-center w-full">
                <div
                  className="text-sm flex items-center"
                  style={{ width: "45%" }}
                >
                  {selectedMergeOption?.label === "Merge By Place Holder" ? (
                    <>{"{{Loan.LoanNumber}}/{{placeholder}}"}</>
                  ) : (
                    <InputText
                      style={{ width: "100%", margin: "0" }}
                      value={packageFieldToken}
                      onChange={(e) => setPackageFieldToken(e.target.value)}
                      className="text-sm"
                    />
                  )}
                </div>
                <div style={{ width: "45%" }}>
                  <InputText
                    style={{ width: "100%", margin: "0" }}
                    id="packageName"
                    {...register("packageName")}
                    name="packageName"
                    onChange={handleChange}
                    className="w-full text-sm"
                  />
                </div>
                <span
                  className="text-sm"
                  style={{ width: "10%", textAlign: "left", paddingLeft: 8 }}
                >
                  .pdf
                </span>
              </div>
              <div className="text-xs text-red-900">
                {errors.packageName?.message}
              </div>
            </div>
          )}

          {/* Loan Number Upload */}
          <div>
            <label htmlFor="loanNumbers" className="py-2 block text-sm">
              Upload Loan Number
            </label>
            <FileUpload
              auto
              name="loanNumbers"
              accept=".csv, .xls, .xlsx, image/*"
              maxFileSize={1000000}
              customUpload
              multiple={false}
              uploadHandler={(e) => {
                const file = e.files[0];
                setLoanFile(file);
                setValue("loanNumbers", file, { shouldValidate: true });
                clearErrors("loanNumbers");
              }}
              onClear={() => {
                setLoanFile(null);
                setValue("loanNumbers", null, { shouldValidate: true });
                clearErrors("loanNumbers");
              }}
              chooseLabel="Select File"
              emptyTemplate={
                <p className="m-0">Drag and drop file to here to upload.</p>
              }
            />
            {errors.loanNumbers && (
              <div className="text-xs text-red-900 mt-1">
                {errors.loanNumbers.message}
              </div>
            )}
          </div>

          {/* Document Type Upload */}
          <div>
            <label htmlFor="documentTypes" className="py-2 block text-sm">
              Upload Document Type
            </label>

            <FileUpload
              auto
              name="documentTypes"
              accept=".csv, .xls, .xlsx, image/*"
              maxFileSize={1000000}
              customUpload
              multiple={false}
              uploadHandler={(e) => {
                const file = e.files[0];
                setDocumentFile(file);
                setValue("documentTypes", file, { shouldValidate: true });
                clearErrors("documentTypes");
              }}
              onClear={() => {
                setDocumentFile(null);
                setValue("documentTypes", null, { shouldValidate: true });
                clearErrors("documentTypes");
              }}
              chooseLabel="Select File"
              emptyTemplate={
                <p className="m-0">Drag and drop file to here to upload.</p>
              }
            />
            {errors.documentTypes && (
              <div className="text-xs text-red-900 mt-1">
                {errors.documentTypes.message}
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-8 flex flex-row-reverse items-center">
          <Button
            label="Submit"
            type="submit"
            icon="pi pi-check"
            className="p-button-sm p-button-success p-button-outlined"
          />
          <div className="mx-2"></div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onClose}
            className="p-button-sm p-button-danger p-button-outlined"
          />
        </div>
      </form>
      {loading && <Loader />}
    </>
  );
};

export default AddJob;
